/**
 * ECharts Option Builders — barrel module.
 *
 * Re-exports the public API so consumers can import from
 * `./optionBuilders` without changing any import paths.
 *
 * Internal structure:
 *   utils.ts        — column helpers, axis formatting, text measurement
 *   seriesData.ts   — category/series extraction for cartesian charts
 *   bar.ts          — bar / histogram builder
 *   line.ts         — line / area builder
 *   pie.ts          — pie / donut builder
 *   funnel.ts       — funnel builder
 *   scatter.ts      — scatter builder
 *   heatmap.ts      — heatmap builder
 *   chartStyle — applyChartStyle overlay
 */
import type { EChartsOption } from 'echarts';
import type { ChartTheme } from '../theme';
import type { ChartData, ChartColumn, ChartMeta } from '../../../../api/types';

import { buildBarChart } from './bar';
import { buildLineChart } from './line';
import { buildPieChart } from './pie';
import { buildFunnelChart } from './funnel';
import { buildScatterChart } from './scatter';
import { buildHeatmapChart } from './heatmap';

// Re-export for consumers that import from this module
export type { ChartData, ChartColumn, ChartMeta };

// Re-export chart style overlay
export { applyChartStyle } from './visualConfig';

// ── Main dispatcher ────────────────────────────────────────────────────────

/**
 * Build an ECharts option from unified ChartData.
 *
 * @param data           Unified chart data from the server
 * @param theme          Light/dark chart theme
 * @param animated       Whether to enable animation
 * @param containerWidth Container width in px (for auto-rotation). Defaults to 400.
 * @returns              EChartsOption or null if data is invalid/empty
 */
export function buildOption(
  data: ChartData,
  theme: ChartTheme,
  animated: boolean,
  containerWidth: number = 400,
): EChartsOption | null {
  if (!data.rows.length || !data.columns.length) return null;

  switch (data.chartType) {
    case 'line':
      return buildLineChart(data, theme, animated, false, containerWidth);
    case 'area':
      return buildLineChart(data, theme, animated, true, containerWidth);
    case 'bar':
      return buildBarChart(data, theme, animated, false, containerWidth);
    case 'histogram':
      return buildBarChart(data, theme, animated, true, containerWidth);
    case 'pie':
      return buildPieChart(data, theme, animated);
    case 'funnel':
      return buildFunnelChart(data, theme, animated);
    case 'scatter':
      return buildScatterChart(data, theme, animated);
    case 'heatmap':
      return buildHeatmapChart(data, theme, animated);
    default:
      return null;
  }
}